import React, { useState } from 'react';
import { Spin, Drawer, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DataTable, PageLayout } from '../../ui';
import {
  useGetLogistsControlSummaryQuery,
  useGetLogistRoutesWithIssuesQuery,
  type LogistControlSummary,
  type LogistRouteWithIssues
} from '../../store/api/financeApi';
import { formatResponsibleUserLabel } from '../../utils/userDisplayName';

const LogistsControlPage: React.FC = () => {
  const { data, isLoading } = useGetLogistsControlSummaryQuery({});
  const [drawerLogistId, setDrawerLogistId] = useState<number | 'unassigned' | null>(null);
  const [drawerLogistName, setDrawerLogistName] = useState('');

  const { data: routesData, isLoading: routesLoading } = useGetLogistRoutesWithIssuesQuery(
    { logistId: drawerLogistId! },
    { skip: drawerLogistId === null }
  );

  React.useEffect(() => {
    console.log('PHASE I.1 RESPONSIBILITY LAYER READY');
  }, []);

  React.useEffect(() => {
    console.log('PHASE I.2 DRILL-DOWN READY');
  }, []);

  const handleRowClick = (record: LogistControlSummary) => {
    const lid = record.logistId ?? 'unassigned';
    setDrawerLogistId(lid);
    setDrawerLogistName(
      record.logist ? formatResponsibleUserLabel(record.logist) : record.logistName
    );
  };

  const countByResponsible = (route: LogistRouteWithIssues) => {
    const all = [...(route.controlIssues?.critical ?? []), ...(route.controlIssues?.warnings ?? []), ...(route.controlIssues?.info ?? [])];
    return {
      finance: all.filter((i) => i.responsible === 'FINANCE').length,
      logist: all.filter((i) => i.responsible === 'LOGIST').length,
      carrier: all.filter((i) => i.responsible === 'CARRIER').length
    };
  };

  const drawerColumns: ColumnsType<LogistRouteWithIssues> = [
    { title: 'Маршрут', dataIndex: 'routeNumber', key: 'routeNumber', render: (v: any) => `#${v}` },
    { title: 'Тип', dataIndex: 'transportType', key: 'transportType', width: 100 },
    { title: 'Статус', dataIndex: 'status', key: 'status', width: 100 },
    {
      title: 'Критичные',
      key: 'critical',
      width: 90,
      render: (_: any, r: LogistRouteWithIssues) => {
        const n = r.controlIssues?.critical?.length ?? 0;
        return n > 0 ? <Tag color="red">{n}</Tag> : '—';
      }
    },
    {
      title: 'Предупреждения',
      key: 'warnings',
      width: 120,
      render: (_: any, r: LogistRouteWithIssues) => {
        const n = r.controlIssues?.warnings?.length ?? 0;
        return n > 0 ? <Tag color="gold">{n}</Tag> : '—';
      }
    },
    {
      title: 'Финансовые',
      key: 'finance',
      width: 100,
      render: (_: any, r: LogistRouteWithIssues) => {
        const n = countByResponsible(r).finance;
        return n > 0 ? <Tag color="blue">{n}</Tag> : '—';
      }
    },
    {
      title: 'Проблемы логиста',
      key: 'logist',
      width: 130,
      render: (_: any, r: LogistRouteWithIssues) => {
        const n = countByResponsible(r).logist;
        return n > 0 ? <Tag>{n}</Tag> : '—';
      }
    },
    {
      title: 'Проблемы перевозчика',
      key: 'carrier',
      width: 150,
      render: (_: any, r: LogistRouteWithIssues) => {
        const n = countByResponsible(r).carrier;
        return n > 0 ? <Tag>{n}</Tag> : '—';
      }
    }
  ];

  const columns: ColumnsType<LogistControlSummary> = [
    {
      title: 'Логист',
      key: 'logistName',
      render: (_: unknown, record: LogistControlSummary) =>
        record.logist ? formatResponsibleUserLabel(record.logist) : record.logistName,
    },
    { title: 'Маршрутов', dataIndex: 'totalRoutes', key: 'totalRoutes', width: 100 },
    { title: 'С проблемами', dataIndex: 'routesWithIssues', key: 'routesWithIssues', width: 120 },
    { title: 'Критичные', dataIndex: 'criticalCount', key: 'criticalCount', width: 100 },
    { title: 'Проблемы логиста', dataIndex: 'logistIssues', key: 'logistIssues', width: 140 },
    { title: 'Проблемы перевозчика', dataIndex: 'carrierIssues', key: 'carrierIssues', width: 160 },
    { title: 'Финансовые проблемы', dataIndex: 'financeIssues', key: 'financeIssues', width: 160 }
  ];

  if (isLoading) {
    return (
      <PageLayout title="Контроль логистов" subtitle="Загрузка сводки…">
        <div className="text-center py-12">
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Контроль логистов" subtitle="Маршруты с нарушениями по ответственным логистам">
      <DataTable
        rowKey={(r) => String(r.logistId ?? 'unassigned')}
        columns={columns}
        dataSource={data?.logists ?? []}
        pagination={false}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
      />

      <Drawer
        title={`Маршруты с нарушениями: ${drawerLogistName}`}
        placement="right"
        width={720}
        onClose={() => setDrawerLogistId(null)}
        open={drawerLogistId !== null}
      >
        {routesLoading ? (
          <Spin />
        ) : !routesData?.routes?.length ? (
          <div>У логиста нет маршрутов с нарушениями</div>
        ) : (
          <DataTable
            rowKey="routeId"
            columns={drawerColumns}
            dataSource={routesData.routes}
            pagination={false}
            size="small"
          />
        )}
      </Drawer>
    </PageLayout>
  );
};

export default LogistsControlPage;
